import React, { memo, useCallback, useState } from 'react';
// import { MaterialIcons } from '@expo/vector-icons';
import { useTheme } from 'tamagui';
import { GroupComment } from '@cobu/types';
import { CobuView, CobuAlert } from '@cobu/ui';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import CommentActionSheet from './CommentActionSheet';
import { Strings } from '../../../constants';

interface CommentActionsProps {
  comment: GroupComment;
  isCurrentUser: boolean;
  isPinned: boolean;
  allowEditing: boolean;
  allowReporting: boolean;
  onEdit?: (comment: GroupComment) => void;
  onDelete?: () => void;
  onReport?: (userId: string) => void;
  onBlock?: () => void;
  onFlag?: () => void;
  onHidePinned?: () => void;
}

const CommentActions: React.FC<CommentActionsProps> = memo(({
  comment,
  isCurrentUser,
  isPinned,
  allowEditing,
  allowReporting,
  onEdit,
  onDelete,
  onReport,
  onBlock,
  onFlag,
  onHidePinned,
}) => {
  const theme = useTheme();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const [showFlag, setShowFlag] = useState(false);
  const { commentActions } = Strings.get().discussion;

  const handleShowActionSheet = useCallback(() => {
    // Defer state change to avoid press/gesture timing issues
    setTimeout(() => setIsSheetOpen(true), 0);
  }, []);

  const openAfterSheetCloses = (setter: (v: boolean) => void) => {
    setIsSheetOpen(false);
    setTimeout(() => setter(true), 50);
  };

  if (isPinned && onHidePinned) {
    return (
      <CobuView onPress={onHidePinned} >
        <CloseIcon />
      </CobuView>
    );
  }

  if ((isCurrentUser && allowEditing && onDelete) || (!isCurrentUser && (onReport || onBlock || onFlag))) {
    return (
      <>
        <CobuView onPress={handleShowActionSheet}>
          <MoreHorizIcon width={20} height={20} color={theme.iconColor?.val} />
        </CobuView>
        {isSheetOpen && (
        <CommentActionSheet
          comment={comment}
          isCurrentUser={isCurrentUser}
          canReport={allowReporting}
          onDelete={onDelete}
          onReport={onReport}
          onBlock={onBlock}
          onFlag={onFlag}
          onClose={() => setIsSheetOpen(false)}
          onRequestDelete={() => openAfterSheetCloses(setShowDelete)}
          onRequestBlock={() => openAfterSheetCloses(setShowBlock)}
          onRequestFlag={() => openAfterSheetCloses(setShowFlag)}
        />)}

        {/* Delete confirmation */}
        <CobuAlert
          open={showDelete}
          onOpenChange={setShowDelete}
          title={commentActions.deleteConfirm.title}
          description={commentActions.deleteConfirm.message}
          acceptText={commentActions.labels.deleteComment}
          cancelText={commentActions.labels.cancel}
          onAccept={() => {
            setShowDelete(false);
            onDelete?.();
          }}
          onCancel={() => setShowDelete(false)}
        />

        {/* Block confirmation */}
        <CobuAlert
          open={showBlock}
          onOpenChange={setShowBlock}
          title={commentActions.blockConfirm.title.replace('{displayName}', comment.user.displayName)}
          description={commentActions.blockConfirm.message}
          acceptText={commentActions.labels.blockUser}
          cancelText={commentActions.labels.cancel}
          onAccept={() => {
            setShowBlock(false);
            onBlock?.();
          }}
          onCancel={() => setShowBlock(false)}
        />

        {/* Flag confirmation */}
        <CobuAlert
          open={showFlag}
          onOpenChange={setShowFlag}
          title={commentActions.flagConfirm.title}
          description={commentActions.flagConfirm.message}
          acceptText={commentActions.labels.flagPost}
          cancelText={commentActions.labels.cancel}
          onAccept={() => {
            setShowFlag(false);
            onFlag?.();
          }}
          onCancel={() => setShowFlag(false)}
        />
      </>
    );
  }

  return null;
});

CommentActions.displayName = 'CommentActions';

export default CommentActions; 